/**
 * 用戶上下文 Hook
 * 提供獲取和設置用戶當前上下文（專案和頁面）的功能
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { contextService, UserContext } from "@/domain/services/contextService";

/**
 * 用戶上下文 Hook 返回值介面
 */
interface UseUserContextReturn {
  context: UserContext;
  refreshContext: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  setProjectContext: (projectId: string) => Promise<void>;
  setPageContext: (pageId: string) => Promise<void>;
  navigateToChat: () => void;
}

/**
 * 用戶上下文 Hook
 * 管理用戶當前上下文（專案和頁面）的狀態和操作
 */
export function useUserContext(): UseUserContextReturn {
  const navigate = useNavigate();
  const [context, setContext] = useState<UserContext>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchContext = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const userContext = await contextService.getContext();
      setContext(userContext);
    } catch (err) {
      console.error("Failed to fetch user context:", err);
      setError("無法獲取用戶上下文");
    } finally {
      setIsLoading(false);
    }
  };

  // 載入用戶上下文
  useEffect(() => {
    fetchContext();
  }, []);

  const refreshContext = async () => {
    await fetchContext();
  };

  // 設置專案上下文
  const setProjectContext = useCallback(
    async (projectId: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const updatedContext = await contextService.setCurrentContext({
          ...context,
          current_project_id: projectId,
        });

        setContext(updatedContext);
        contextService.saveLocalContext(updatedContext);
      } catch (err) {
        console.error("Failed to set project context:", err);
        setError("無法設置專案上下文");
      } finally {
        setIsLoading(false);
      }
    },
    [context],
  );

  // 設置頁面上下文
  const setPageContext = useCallback(
    async (pageId: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const updatedContext = await contextService.setCurrentContext({
          ...context,
          current_page_id: pageId,
        });

        setContext(updatedContext);
        contextService.saveLocalContext(updatedContext);
      } catch (err) {
        console.error("Failed to set page context:", err);
        setError("無法設置頁面上下文");
      } finally {
        setIsLoading(false);
      }
    },
    [context],
  );

  // 導航到聊天頁面，使用當前上下文
  const navigateToChat = useCallback(() => {
    let chatUrl = "/chat";
    const params = new URLSearchParams();

    if (context.current_page_id) {
      params.append("pageId", context.current_page_id);
    } else if (context.current_project_id) {
      params.append("projectId", context.current_project_id);
    }

    if (params.toString()) {
      chatUrl += `?${params.toString()}`;
    }

    navigate(chatUrl);
  }, [context, navigate]);

  return {
    context,
    isLoading,
    error,
    setProjectContext,
    setPageContext,
    refreshContext,
    navigateToChat,
  };
}
